import { useCallback, useMemo } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { toastManager } from "@salestrak/ui";
import {
  ArchiveReasonDocument,
  ClearDefaultReasonDocument,
  connectionRows,
  CreateReasonDocument,
  DeleteReasonDocument,
  GetReasonsDocument,
  type ReasonRow,
  SetDefaultReasonDocument,
  UnarchiveReasonDocument,
  UpdateReasonDocument,
} from "#app/lib/data";
import { addReasonToCache, removeReasonFromCache } from "./reason-cache-utils";
import { toastMutationError } from "#app/lib/utils/toast-errors";

export type Reason = ReasonRow;

const REASONS_QUERY_VARS = { limit: 50, orderBy: { name: 1 } };

export function useReasons() {
  const { data, loading, error } = useQuery(GetReasonsDocument, {
    variables: REASONS_QUERY_VARS,
    fetchPolicy: "cache-and-network",
  });

  const reasons = useMemo<Reason[]>(
    () => connectionRows(data?.reasons),
    [data],
  );

  return {
    reasons,
    isLoading: loading,
    error,
  };
}

export function useReasonMutations() {
  const [createMutation] = useMutation(CreateReasonDocument);
  const [updateMutation] = useMutation(UpdateReasonDocument);
  const [archiveMutation] = useMutation(ArchiveReasonDocument);
  const [unarchiveMutation] = useMutation(UnarchiveReasonDocument);
  const [deleteMutation] = useMutation(DeleteReasonDocument);
  const [setDefaultMutation] = useMutation(SetDefaultReasonDocument);
  const [clearDefaultMutation] = useMutation(ClearDefaultReasonDocument);

  const createReason = useCallback(
    async (name: string) => {
      try {
        const { data } = await createMutation({
          variables: { input: { name } },
          update(cache, { data }) {
            const result = data?.createReason;
            if (result?.__typename !== "Reason") return;
            addReasonToCache(cache, result);
          },
        });
        const result = data?.createReason;
        if (result?.__typename === "Reason") {
          toastManager.add({ title: "Reason created", type: "success" });
          return result;
        }
        if (result?.__typename === "ValidationError") {
          toastManager.add({ title: result.message ?? "Failed to create reason", type: "error" });
          throw new Error(result.message);
        }
        toastManager.add({ title: "Failed to create reason", type: "error" });
        return null;
      } catch (err) {
        toastMutationError(err, "Failed to create reason");
        return null;
      }
    },
    [createMutation],
  );

  const updateReason = useCallback(
    async (id: string, name: string) => {
      try {
        const { data } = await updateMutation({
          variables: { id, input: { name } },
        });
        const result = data?.updateReason;
        if (result?.__typename === "ValidationError") {
          toastManager.add({ title: result.message ?? "Failed to update reason", type: "error" });
          throw new Error(result.message);
        }
        if (result?.__typename === "Reason") {
          toastManager.add({ title: "Reason updated", type: "success" });
          return result;
        }
        toastManager.add({ title: "Failed to update reason", type: "error" });
        return null;
      } catch (err) {
        toastMutationError(err, "Failed to update reason");
        return null;
      }
    },
    [updateMutation],
  );

  const toggleReason = useCallback(
    async (reason: Reason) => {
      try {
        if (reason.archived) {
          await unarchiveMutation({ variables: { id: reason.id } });
        } else {
          await archiveMutation({ variables: { id: reason.id } });
        }
        toastManager.add({ title: reason.archived ? "Reason enabled" : "Reason disabled", type: "success" });
      } catch (err) {
        toastMutationError(err, "Failed to update reason");
      }
    },
    [archiveMutation, unarchiveMutation],
  );

  const deleteReason = useCallback(
    async (reason: Reason) => {
      try {
        await deleteMutation({
          variables: { id: reason.id },
          update(cache, { data }) {
            const result = data?.deleteReason;
            if (result?.__typename !== "Reason") return;
            removeReasonFromCache(cache, result);
          },
        });
        toastManager.add({ title: "Reason deleted", type: "success" });
      } catch (err) {
        toastMutationError(err, "Failed to delete reason");
      }
    },
    [deleteMutation],
  );

  const setDefaultReason = useCallback(
    async (id: string) => {
      try {
        await setDefaultMutation({
          variables: { id },
          refetchQueries: ["GetReasons"],
        });
        toastManager.add({ title: "Default reason updated", type: "success" });
      } catch (err) {
        toastMutationError(err, "Failed to update default reason");
      }
    },
    [setDefaultMutation],
  );

  const clearDefaultReason = useCallback(
    async () => {
      try {
        await clearDefaultMutation({
          refetchQueries: ["GetReasons"],
        });
        toastManager.add({ title: "Default reason cleared", type: "success" });
      } catch (err) {
        toastMutationError(err, "Failed to clear default reason");
      }
    },
    [clearDefaultMutation],
  );

  return {
    createReason,
    updateReason,
    toggleReason,
    deleteReason,
    setDefaultReason,
    clearDefaultReason,
  };
}
